import 'chartjs-plugin-datalabels'; // Plugin for adding number overlays on the bar/pie slices

import {
  FluidChartInterface,
  FluidPieChartTranslationOptions,
  FluidTheme,
  FluidThemeInterface,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import { generateUniqueId } from '@lmig/fluid-core/lib/helpers/string.helpers';
import {
  Component,
  ComponentInterface,
  Element,
  h,
  Prop,
  State,
  Watch,
} from '@stencil/core';
import { Chart, ChartSize } from 'chart.js'; // Charting library, uses HTML canvas

import { ChartTitle } from '../../common/fluid-chart.components';
import { DARK_BLUE, WHITE } from '../../constants/fluid-chart.constants';
import { useI18n } from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import {
  ChartUtils,
  FluidChartStyles,
} from '../../utils/charts/fluid-chart-utils';
import { Utils } from '../../utils/utils';
import {
  ContainedChart,
  PieChartLegend,
  ResponsiveChart,
} from './components/fluid-pie-chart.components';

/**
 * @displayName Pie Chart
 *
 * @contextData FLUID Pie Chart is a data visualization component used to show how parts of a data set add up to a total value. It is typically used to illustrate numerical proportions and percentages.
 */
@Component({
  tag: 'fluid-pie-chart',
  styleUrl: 'fluid-pie-chart.scss',
  shadow: true,
})
export class FluidPieChart
  implements FluidThemeInterface, FluidChartInterface, ComponentInterface
{
  // ================================================================ //
  // -- Own Properties (Private)

  /**
   * Intrinsic/extrinsic width of the chart.
   */
  private _elementWidth: number;

  /**
   * Reference to the <canvas> element that is used to render the chart.
   */
  private _chartRef: HTMLCanvasElement;

  /**
   * Reference to the Chart.js chart instance.
   */
  private _chart: Chart;

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidPieChartElement;

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: FluidPieChartTranslationOptions;
  private _translateId: string = generateUniqueId();
  @State() private _i18nStore: FluidI18nStore | undefined;

  // ================================================================ //
  // -- States

  /**
   * Current font size of the Chart's legend items.
   */
  @State() _legendFontSize: number;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * Make sure that the chart colors reflect the legend. For some reason,
   * the chart colors will set to LM and then the chart will switch to
   * legacy. This is a hacky solution to that issue, will update the
   * colors and re-render the chart when the legend is updated.
   */
  @Watch('theme')
  themeWatchHandler(newValue: string, oldValue: string) {
    if (newValue !== oldValue && this._chart && !this.colorOverride) {
      const { colors } = FluidChartStyles[this.theme];
      const chartLabelColors = colors.map((x) =>
        ChartUtils.pickTextColor(x, WHITE, DARK_BLUE)
      );

      this._chart.data.datasets[0].backgroundColor = colors;
      this._chart.options.plugins.datalabels.color = chartLabelColors;
      this._chart.options.plugins.datalabels.backgroundColor = colors;
      this._chart.update();
      this._chart.render();
    }
  }

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * A unique identifier for your chart canvas element
   * Useful to specify for E2E, analytics, etc.
   * Defaults to a unique 36 char string
   */
  @Prop({ mutable: true }) chartId: string = generateUniqueId();

  /**
   * When provided, displays a heading title above the chart.
   */
  @Prop({ mutable: true }) chartTitle: string;

  /**
   * Array of numbers for chart values. The chart will calculate
   * the relative percentage
   */
  @Prop({ mutable: true }) chartData: number[];

  @Watch('chartData')
  chartDataWatchHandler(newValue: number[], oldValue: number[]) {
    if (newValue !== oldValue) {
      let { colors } = FluidChartStyles[this.theme];

      if (this.colorOverride) {
        colors = ChartUtils.createColorsSequence(
          this.colorOverride,
          this.chartData as []
        );
      }

      ChartUtils.dataHandler(this._chart, newValue, null, colors);
    }
  }

  /**
   * Array of labels for each chart value
   */
  @Prop({ mutable: true }) chartLabels: string[];

  @Watch('chartLabels')
  chartLabelsWatchHandler(newValue: string[], oldValue: string[]) {
    if (newValue !== oldValue) ChartUtils.labelHandler(this._chart, newValue);
  }

  /**
   * Array of objects to handle any possible data required
   * for the hover tooltip
   */
  @Prop({ mutable: true }) chartHoverData: object[];

  @Watch('chartHoverData')
  chartHoverDataWatchHandler(newValue: object[], oldValue: object[]) {
    if (newValue !== oldValue)
      this._chart.options.tooltips.callbacks.afterBody = (tooltipItems) =>
        ChartUtils.tooltipAfterBody(tooltipItems, newValue);
  }

  /**
   * When provided, adds a white background with subtle drop shadow
   * to the chart's container.
   */
  @Prop({ mutable: true }) hasBackground = false;

  /**
   * Lets the user decide if they want a label
   * Defaults to true
   */
  @Prop({ mutable: true }) hasDataLabels = true;

  /**
   * Lets the user decide the orientation of the legend
   */
  @Prop({ mutable: true }) legendOrientation: 'horizontal' | 'vertical' =
    'horizontal';

  /**
   * Lets the user decide if they want a legend
   */
  @Prop({ mutable: true }) hasLegend = true;

  /**
   * Lets the user decide the size of the title text
   *
   */
  @Prop({ mutable: true }) titleFontTextSize: number;

  /**
   * Lets the user decide the height of the chart
   *
   */
  @Prop({ mutable: true }) chartHeight: number;

  /**
   * Lets the user decide the width of the chart
   *
   */
  @Prop({ mutable: true }) chartWidth: number;

  /**
   * Allows the user to change the chart type to either "pie" (default)
   * or "doughnut";
   */
  @Prop({ mutable: true }) chartType: 'pie' | 'doughnut' = 'pie';

  /**
   * Allows the user to pass in a custom color scheme for the line graph.
   * If prop is not given the color scheme will default to styles in FluidChartStyles
   * When this is set the theme colors will be ignored
   */
  @Prop({ mutable: true }) colorOverride: string[];

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   */
  connectedCallback(): void {
    useI18n.bind(this)({
      language: () => this.setChartLabels(),
    });
    Utils.setGlobalTheme(this);

    // @ts-ignore - Fix US spelling
    if (this.chartType === 'donut') {
      this.chartType = 'doughnut';
    }
  }

  /**
   * Called once just after the component is first connected to the DOM.
   */
  componentWillLoad() {
    this.tooltipFontTextSizeDeprecationWarning();
    this.legendFontTextSizeDeprecationWarning();
  }

  /**
   * Lifecycle Method to create the chart once the component loads
   *  data - takes in at least one dataset
   *  options - for styling and tweaking chart settings
   */
  componentDidLoad() {
    this._elementWidth = this.host.clientWidth;
    this._legendFontSize = ChartUtils.getChartBodyFontSize(this._elementWidth);
    let { colors } = FluidChartStyles[this.theme];
    const { fonts } = FluidChartStyles[this.theme];

    // override colors and generate a colors sequence
    if (this.colorOverride) {
      colors = ChartUtils.createColorsSequence(
        this.colorOverride,
        this.chartData as []
      );
    }

    // Declare the chart label colors scheme for proper contrast
    const chartLabelColors = colors.map((x) =>
      ChartUtils.pickTextColor(x, WHITE, DARK_BLUE)
    );

    const myChartRef = this._chartRef.getContext('2d');
    this._chart = new Chart(myChartRef, {
      type: this.chartType,
      data: {
        labels:
          this._i18nStore?.translate(this.fluidTranslate?.chartLabels) ||
          this.chartLabels,
        datasets: [
          {
            data: this.chartData,
            backgroundColor: colors,
          },
        ],
      },
      options: {
        plugins: {
          // Controls the datalabels plugin
          datalabels: {
            // Will display a label if it can fit in a pie slice
            display: (ctx) => {
              if (this.hasDataLabels) {
                const data: any[] = ctx.chart.data.datasets[0].data;
                const value: any = ctx.dataset.data[ctx.dataIndex];
                const sum = data.reduce((a, b) => a + b, 0);
                const percentage = Math.round((value / sum) * 100);
                return percentage > 5;
              } else return false;
            },
            color: chartLabelColors,
            font: (context) => ({
              size: ChartUtils.fontLabelScaler(context.chart.width),
              weight: 'bold',
              family: fonts,
            }),
            formatter: (value, ctx) => {
              const data: any[] = ctx.chart.data.datasets[0].data;
              const sum = data.reduce((a, b) => a + b, 0);
              return Math.round((value / sum) * 100) + '%';
            },
          },
        },
        layout: {
          padding: {
            left: 0,
            right: 0,
            top: 8,
            bottom: 0,
          },
        },
        responsive: true,
        maintainAspectRatio: !this.chartHeight,
        aspectRatio: 4 / 3,
        title: {
          display: false, // We are using a custom element
        },
        legend: {
          display: false, // We are using a custom element
        },
        elements: {
          arc: {
            borderWidth: 0,
          },
        },
        tooltips: {
          titleFontFamily: fonts,
          titleFontSize: ChartUtils.getChartBodyFontSize(this._elementWidth),
          bodyFontFamily: fonts,
          bodyFontSize:
            this.tooltipFontTextSize ??
            ChartUtils.getChartBodyFontSize(this._elementWidth),
          callbacks: {
            label: (tooltipItems, data) => {
              const category =
                this._i18nStore?.translate(
                  this.fluidTranslate?.chartLabels[tooltipItems.index]
                ) || data.labels[tooltipItems.index];
              const value: any = data.datasets[0].data[tooltipItems.index];
              const theData: any[] = data.datasets[0].data;
              const sum = theData.reduce((a, b) => a + b, 0);
              const percentage = Math.round((value / sum) * 100) + '%';
              return `${category}: ${percentage}`;
            },
            // Modifies hover tooltip element with anything hoverData passes, designed to handle unlimited entries
            afterBody: (tooltipItems) =>
              ChartUtils.tooltipAfterBody(
                tooltipItems,
                this._i18nStore?.translate(
                  this.fluidTranslate?.chartHoverData
                ) || this.chartHoverData
              ),
          },
        },
        // @ts-ignore - This is a bug in the typings, the function signature is correct
        onResize: (chartInstance: Chart, size: ChartSize): void => {
          if (isNullOrUndefined(chartInstance)) return;
          this._elementWidth = size.width;
          this._legendFontSize = ChartUtils.getChartBodyFontSize(
            this._elementWidth
          );

          if (!this.tooltipFontTextSize) {
            ChartUtils.updateChart(chartInstance);
          } else {
            chartInstance.options.tooltips.titleFontSize =
              this.tooltipFontTextSize;
            chartInstance.options.tooltips.bodyFontSize =
              this.tooltipFontTextSize;
            chartInstance.update();
            chartInstance.render();
          }
        },
      },
    });

    this._chart.render();
  }

  /**
   * Called every time the component is disconnected from the DOM.
   */
  disconnectedCallback() {
    this._i18nStore?.deregisterElement(this._translateId);
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Updates the chart labels whenever the language changes in the i18n provider.
   */
  setChartLabels() {
    this._chart.data.labels =
      this._i18nStore?.translate(this.fluidTranslate?.chartLabels) ||
      this.chartLabels;
    this._chart.update();
  }

  /**
   * Used for calculating relative sizes
   * @return the chart element
   */
  getChart() {
    return this._chart;
  }

  /**
   * Used for calculating relative sizes
   * @return The width of the containing element
   */
  getElementWidth(): number {
    return this._elementWidth;
  }

  /**
   * Creates a Legend Component, which will run if supplied with the correct data
   * Custom legend is included for readability, allows the user to quickly see the pie splice percentage
   */
  renderLegend(): HTMLElement {
    if (!this.hasLegend) return;

    if (isNullOrUndefined(this.chartData)) {
      return (
        <h3>
          {this._i18nStore?.translate(this.fluidTranslate?.loadFailure) ||
            'Failure to load legend'}
        </h3>
      );
    }

    const legendFontSize = this.legendFontTextSize || this._legendFontSize;
    if (!legendFontSize) return;

    let { colors } = FluidChartStyles[this.theme];
    if (this.colorOverride) {
      colors = ChartUtils.createColorsSequence(
        this.colorOverride,
        this.chartData as []
      );
    }

    // Get an array of the percentages as a number for each pie slice
    const sum = this.chartData.reduce(
      (result, dataPoint) => result + dataPoint,
      0
    );

    // Turn each decimal into a string x.y%
    const percentageArray = this.chartData.map(
      (dataItem: number) => `${((dataItem / sum) * 100).toFixed(1)}%`
    );

    // Turn that into the final string to be rendered
    const translatedLabels =
      this._i18nStore?.translate(this.fluidTranslate?.chartLabels) ||
      this.chartLabels;
    const legendItems = percentageArray.map(
      (item: string, idx: number) => `${translatedLabels[idx]}: ${item}`
    );

    return (
      <PieChartLegend
        legendItems={legendItems}
        colors={colors}
        orientation={this.legendOrientation}
        legendFontSize={legendFontSize}
      />
    );
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    return (
      <div
        class={{
          [`fluid-pie-chart theme-${this.theme}`]: true,
          'has-background': this.hasBackground,
        }}>
        <div class='container'>
          <ChartTitle
            title={this.chartTitle}
            fluidTranslate={this.fluidTranslate}
            i18nStore={this._i18nStore}
            fontSize={this.titleFontTextSize}
            theme={this.theme}
          />
          {this.chartHeight || this.chartWidth ? (
            <ContainedChart
              chartHeight={this.chartHeight}
              chartWidth={this.chartWidth}
              chartId={this.chartId}
              setRef={(el: HTMLCanvasElement) => (this._chartRef = el)}
            />
          ) : (
            <ResponsiveChart
              chartId={this.chartId}
              setRef={(el: HTMLCanvasElement) => (this._chartRef = el)}
            />
          )}
          {this.renderLegend()}
        </div>
      </div>
    );
  }

  // ================================================================ //
  // -- Deprecated Props

  /**
   * DEPRECATED: "tooltipFontTextSize" is now deprecated. Tooltip font
   * size is now responsive & is calculated based on the width of the
   * chart. -- Lets the user decide the size of the tooltip text.
   * @deprecated
   */
  @Prop({ mutable: true }) tooltipFontTextSize: number;

  @Watch('tooltipFontTextSize')
  tooltipFontTextSizeDeprecationWarning() {
    if (isNullOrUndefined(this.tooltipFontTextSize)) return;
    Utils.consoleWarn(
      this.host,
      '"tooltipFontTextSize" Prop is deprecated. Chart tooltip font size is now responsive & is automatically calculated based on the chart\'s width. If this Prop is provided, the chart\'s tooltips will NOT be responsive.'
    );
  }

  /**
   * DEPRECATED: "legendFontTextSize" is now deprecated. Legend font
   * size is now responsive & is calculated based on the width of the
   * chart. -- Lets the user decide the size of the legend text.
   * @deprecated
   */
  @Prop({ mutable: true }) legendFontTextSize: number;

  @Watch('legendFontTextSize')
  legendFontTextSizeDeprecationWarning() {
    if (isNullOrUndefined(this.legendFontTextSize)) return;
    Utils.consoleWarn(
      this.host,
      '"legendFontTextSize" Prop is deprecated. Chart legend font size is now responsive & is automatically calculated based on the chart\'s width. If this Prop is provided, the chart\'s legend will NOT be responsive.'
    );
  }
}
